import { Accordion, useAccordionItemContext } from '@chakra-ui/react';
import {
  ActiveDocumentType,
  ActiveItemTypeToMetaDataMap,
  DocumentItemState,
  IDefaultQuoteDocumentsSchema,
  QuoteDocumentActiveItemType,
  QuoteDocumentActiveItemTypeToConfigurationKey,
} from '@monetize/types/app';
import { nullifyEmptyStrings } from '@monetize/utils/core';
import { useEffect, useState } from 'react';
import { Link as ReactDomLink } from 'react-router-dom';
import {
  useDefaultDocumentRemover,
  useDefaultDocumentUploader,
} from '../../../../../api/settingsService';
import {
  MAccordion,
  MAccordionCustomButton,
  MAccordionItem,
  MButton,
  MDivider,
  MGrid,
  MGridItem,
  MLink,
  MSkeleton,
  MStack,
  MText,
} from '../../../../../components/Monetize';
import { COMPANY_SETTINGS } from '../../../../../constants/routes';
import { useAccordionControl } from '../../../../../hooks';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../../../hooks/useSettingsSectionHighlight';
import { useFlags } from '../../../../../services/launchDarkly';
import {
  convertToBase64,
  showPdfInNewTab,
} from '../../../../../utils/download';
import { arrayToObject } from '../../../../../utils/misc';
import { QuoteDocumentsUploadItem } from '../../../../Quotes/Quote/components/quoteDocuments/QuoteDocumentsUploadItem';
import { QUOTE_SETTING_KEYS } from '../quoteSettingsConfig';
import { TenantQuoteDocumentConfigurations } from './TenantQuoteDocumentConfigurations';

export interface IDefaultQuoteDocumentsProps {
  isLoading: boolean;
  defaultQuoteDocuments: IDefaultQuoteDocumentsSchema[];
}

interface DefaultQuoteDocumentsBodyProps {
  enableTenantQuoteDocumentManagement: boolean;
  enableQuoteDocumentManagement: boolean;
  isUploadingDocument: boolean;
  msaData: DocumentItemState;
  pdfCoverData: DocumentItemState;
  pdfFooterData: DocumentItemState;
  handleActiveItemChange: (
    type: QuoteDocumentActiveItemType,
    item: ActiveDocumentType,
  ) => void;
  handleAddItem: (
    type: QuoteDocumentActiveItemType,
    item: { type: 'file'; value: File } | { type: 'link'; value: string },
  ) => Promise<void>;
  handleRemoveItem: (type: QuoteDocumentActiveItemType) => void;
  handleGoToItem: (type: QuoteDocumentActiveItemType) => Promise<void>;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const DefaultQuoteDocumentsBody = ({
  enableTenantQuoteDocumentManagement,
  enableQuoteDocumentManagement,
  isUploadingDocument,
  msaData,
  pdfCoverData,
  pdfFooterData,
  handleActiveItemChange,
  handleAddItem,
  handleRemoveItem,
  handleGoToItem,
}: DefaultQuoteDocumentsBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton isExpanded={isExpanded} label="Quote Documents" />
      <Accordion.ItemContent>
        <Accordion.ItemBody>
          {enableTenantQuoteDocumentManagement ? (
            <MText color="tGray.darkPurple">
              Uploaded documents are available across all quotes. Configure
              documents to auto-attach to new quotes or add them manually as
              needed. Documents can also be managed per{' '}
              <MLink asChild color="tIndigo.base">
                <ReactDomLink to={COMPANY_SETTINGS}>Legal Entity</ReactDomLink>
              </MLink>
              .
            </MText>
          ) : (
            <>
              <MText color="tGray.darkPurple">
                The default MSA, cover page and footer will be attached to the
                PDF of all Quotes.
              </MText>
              {enableQuoteDocumentManagement && (
                <MText color="tGray.darkPurple">
                  If you have multiple Legal Entities, Entity-specific documents
                  may be uploaded under the Legal Entities setting.
                </MText>
              )}
            </>
          )}
          {enableTenantQuoteDocumentManagement ? (
            <>
              <MGrid templateColumns="repeat(12, 1fr)" gap={4} mt={6}>
                <MGridItem colSpan={12}>
                  <QuoteDocumentsUploadItem
                    allowLink
                    showControlButton={!msaData.hideBtn}
                    isReadOnly={false}
                    linkUrl={msaData.linkUrl}
                    savedItemText={msaData.savedItemText}
                    allowFileUpload={false}
                    label="Master Service Agreement (MSA)"
                    dndLabel="Drag and Drop your MSA file here"
                    activeItem={msaData.activeItem}
                    onSetActiveItem={(item) =>
                      handleActiveItemChange('msa', item)
                    }
                    onAddItem={(item) => handleAddItem('msa', item)}
                    onRemoveItem={() => handleRemoveItem('msa')}
                    onGotoSavedItem={() => handleGoToItem('msa')}
                    saveBtnContent={({ value, error }) => {
                      return (
                        <MButton
                          isDisabled={!!error || isUploadingDocument}
                          isLoading={isUploadingDocument}
                          onClick={() =>
                            handleAddItem('msa', {
                              type: 'link',
                              value,
                            })
                          }
                        >
                          Save
                        </MButton>
                      );
                    }}
                  />
                </MGridItem>
              </MGrid>
              <MDivider my={4} />
              <TenantQuoteDocumentConfigurations />
            </>
          ) : (
            <MGrid templateColumns="repeat(12, 1fr)" gap={4} mt={6}>
              <MGridItem colSpan={12}>
                <QuoteDocumentsUploadItem
                  allowLink
                  showControlButton={!msaData.hideBtn}
                  isReadOnly={false}
                  linkUrl={msaData.linkUrl}
                  savedItemText={msaData.savedItemText}
                  allowFileUpload={true}
                  label="Master Service Agreement (MSA)"
                  dndLabel="Drag and Drop your MSA file here"
                  activeItem={msaData.activeItem}
                  onSetActiveItem={(item) =>
                    handleActiveItemChange('msa', item)
                  }
                  onAddItem={(item) => handleAddItem('msa', item)}
                  onRemoveItem={() => handleRemoveItem('msa')}
                  onGotoSavedItem={() => handleGoToItem('msa')}
                  saveBtnContent={({ value, error }) => {
                    return (
                      <MButton
                        isDisabled={!!error || isUploadingDocument}
                        isLoading={isUploadingDocument}
                        onClick={() =>
                          handleAddItem('msa', { type: 'link', value })
                        }
                      >
                        Save
                      </MButton>
                    );
                  }}
                />
              </MGridItem>
              <MGridItem colSpan={12}>
                <QuoteDocumentsUploadItem
                  isReadOnly={false}
                  showControlButton={!msaData.hideBtn}
                  label="Cover"
                  dndLabel="Drag and Drop your cover here"
                  activeItem={pdfCoverData.activeItem}
                  linkUrl={pdfCoverData.linkUrl}
                  savedItemText={pdfCoverData.savedItemText}
                  onSetActiveItem={(item) =>
                    handleActiveItemChange('cover', item)
                  }
                  onAddItem={(item) => handleAddItem('cover', item)}
                  onRemoveItem={() => handleRemoveItem('cover')}
                  onGotoSavedItem={() => handleGoToItem('cover')}
                />
              </MGridItem>
              <MGridItem colSpan={12}>
                <QuoteDocumentsUploadItem
                  isReadOnly={false}
                  showControlButton={!msaData.hideBtn}
                  label="Footer"
                  dndLabel="Drag and Drop your footer here"
                  activeItem={pdfFooterData.activeItem}
                  linkUrl={pdfFooterData.linkUrl}
                  savedItemText={pdfFooterData.savedItemText}
                  onSetActiveItem={(item) =>
                    handleActiveItemChange('footer', item)
                  }
                  onAddItem={(item) => handleAddItem('footer', item)}
                  onRemoveItem={() => handleRemoveItem('footer')}
                  onGotoSavedItem={() => handleGoToItem('footer')}
                />
              </MGridItem>
            </MGrid>
          )}
        </Accordion.ItemBody>
      </Accordion.ItemContent>
    </>
  );
};
const DefaultQuoteDocuments = ({
  isLoading,
  defaultQuoteDocuments,
}: IDefaultQuoteDocumentsProps) => {
  const isDocsHighlighted = useSettingsSectionHighlight(
    QUOTE_SETTING_KEYS.quoteDocuments,
  );
  const { mutate: uploadDocument, isPending: isUploadingDocument } =
    useDefaultDocumentUploader({ meta: { showErrorToast: true } });
  const { mutate: removeDocument } = useDefaultDocumentRemover();

  const { enableQuoteDocumentManagement, enableTenantQuoteDocumentManagement } =
    useFlags();

  const [msaData, setMsaData] = useState<DocumentItemState>({
    savedData: {
      storage: undefined,
      link: undefined,
    },
    activeItem: 'none',
    linkUrl: '',
    savedItemText: '',
    hideBtn: false,
  });
  const [pdfCoverData, setPdfCoverData] = useState<DocumentItemState>({
    savedData: {
      storage: undefined,
    },
    activeItem: 'none',
    linkUrl: '',
    savedItemText: '',
    hideBtn: false,
  });

  const [pdfFooterData, setPdfFooterData] = useState<DocumentItemState>({
    savedData: {
      storage: undefined,
    },
    activeItem: 'none',
    linkUrl: '',
    savedItemText: '',
    hideBtn: false,
  });

  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  useEffect(() => {
    if (isDocsHighlighted) {
      setAccordionValue(['0']);
    }
  }, [isDocsHighlighted, setAccordionValue]);

  const documentHandlerMap = {
    setter: {
      msa: setMsaData,
      cover: setPdfCoverData,
      footer: setPdfFooterData,
    },
    getter: {
      msa: msaData,
      cover: pdfCoverData,
      footer: pdfFooterData,
    },
  };

  useEffect(() => {
    const data = arrayToObject(defaultQuoteDocuments, 'key');
    const defaultMsaData =
      data.DEFAULT_DOCUMENT_LOCATION as IDefaultQuoteDocumentsSchema;
    const defaultMsaLink =
      data.DEFAULT_DOCUMENT_URL as IDefaultQuoteDocumentsSchema;
    const defaultPdfCoverData =
      data.DEFAULT_DOCUMENT_COVER as IDefaultQuoteDocumentsSchema;
    const defaultPdfFooterData =
      data.DEFAULT_DOCUMENT_FOOTER as IDefaultQuoteDocumentsSchema;

    if (enableTenantQuoteDocumentManagement) {
      // File upload disabled for MSA: only track link URL, ignore uploaded file
      const msaLinkUrl = defaultMsaLink?.url || '';
      setMsaData((prevValue) => ({
        ...prevValue,
        savedData: {
          link: msaLinkUrl || undefined,
          storage: undefined,
        },
        linkUrl: msaLinkUrl,
        activeItem: msaLinkUrl ? 'link' : 'none',
        savedItemText: msaLinkUrl,
        hideBtn: !!msaLinkUrl,
      }));
    } else {
      if (defaultMsaData) {
        setMsaData((prevValue) => {
          let activeItem: ActiveDocumentType = 'none';
          if (defaultMsaData.storage) {
            activeItem = 'file';
          } else if (defaultMsaData.url) {
            activeItem = 'link';
          }
          return {
            ...prevValue,
            savedData: {
              link: undefined,
              storage: defaultMsaData.storage,
            },
            activeItem,
            savedItemText:
              defaultMsaData.storage?.filename || defaultMsaData.url || '',
            hideBtn: true,
          };
        });
      }
      if (defaultMsaLink) {
        setMsaData((prevValue) => {
          let activeItem: ActiveDocumentType = 'none';
          if (defaultMsaLink.url) {
            activeItem = 'link';
          }
          return {
            ...prevValue,
            activeItem,
            savedData: {
              link: defaultMsaLink.url || '',
              storage: undefined,
            },
            savedItemText: defaultMsaLink.url || '',
            linkUrl: defaultMsaLink.url || '',
            hideBtn: true,
          };
        });
      }
    }
    if (defaultPdfCoverData) {
      setPdfCoverData((prevValue) => {
        let activeItem: ActiveDocumentType = 'none';
        if (defaultPdfCoverData.storage) {
          activeItem = 'file';
        }
        return {
          ...prevValue,
          savedData: {
            link: undefined,
            storage: defaultPdfCoverData.storage,
          },
          activeItem,
          savedItemText: defaultPdfCoverData.storage?.filename || '',
          hideBtn: true,
        };
      });
    }
    if (defaultPdfFooterData) {
      setPdfFooterData((priorData) => {
        let activeItem: ActiveDocumentType = 'none';
        if (defaultPdfFooterData.storage) {
          activeItem = 'file';
        }
        return {
          ...priorData,
          savedData: {
            link: undefined,
            storage: defaultPdfFooterData.storage,
          },
          activeItem,
          savedItemText: defaultPdfFooterData.storage?.filename || '',
          hideBtn: true,
        };
      });
    }
  }, [defaultQuoteDocuments, enableTenantQuoteDocumentManagement]);

  const handleDocumentUpload = (
    type: string,
    extraData: Record<string, string> = {},
    file?: File,
  ) => {
    const formData = new FormData();
    formData.append(
      'metadata',
      new Blob(
        [
          JSON.stringify(
            nullifyEmptyStrings({
              ...ActiveItemTypeToMetaDataMap[type],
              ...extraData,
            }),
          ),
        ],
        {
          type: 'application/json',
        },
      ),
    );
    if (file) {
      formData.append('file', file);
    }
    uploadDocument(formData);
  };

  const handleActiveItemChange = (
    type: QuoteDocumentActiveItemType,
    item: ActiveDocumentType,
  ) => {
    documentHandlerMap.setter[type]((prevValue) => ({
      ...prevValue,
      activeItem: item,
    }));
  };

  const handleAddItem = async (
    type: QuoteDocumentActiveItemType,
    item: { type: 'file'; value: File } | { type: 'link'; value: string },
  ) => {
    if (item.type === 'file') {
      documentHandlerMap.setter[type]((prevValue) => ({
        ...prevValue,
        fileToUpload: {
          file: item.value,
          fileName: item.value.name,
        },
        savedItemText: item.value.name,
        hideBtn: true,
      }));
      handleDocumentUpload(type, {}, item.value);
    } else {
      documentHandlerMap.setter[type]((prevValue) => ({
        ...prevValue,
        linkUrl: item.value,
        savedItemText: '',
        hideBtn: true,
      }));
      handleDocumentUpload(item.type, { documentUrl: item.value });
    }
  };

  const handleRemoveItem = (type: QuoteDocumentActiveItemType) => {
    documentHandlerMap.setter[type]((prevValue) => ({
      ...prevValue,
      fileToUpload: undefined,
      linkUrl: '',
      activeItem: 'none',
      savedItemText: '',
      hideBtn: false,
    }));
    const activeType =
      documentHandlerMap.getter[type].activeItem === 'link' ? 'link' : type;
    removeDocument(QuoteDocumentActiveItemTypeToConfigurationKey[activeType]);
  };

  const handleGoToItem = async (type: QuoteDocumentActiveItemType) => {
    if (documentHandlerMap.getter[type].activeItem === 'file') {
      let base64Data = '';
      let fileName = '';
      if (documentHandlerMap.getter[type].fileToUpload) {
        base64Data = await convertToBase64(
          documentHandlerMap.getter[type].fileToUpload!.file,
        );
        // eslint-disable-next-line prefer-destructuring
        fileName = documentHandlerMap.getter[type].fileToUpload!.fileName;
      } else if (
        documentHandlerMap.getter[type].savedData.storage?.contentBase64Encoded
      ) {
        base64Data =
          documentHandlerMap.getter[type].savedData.storage!
            .contentBase64Encoded!;
        fileName = documentHandlerMap.getter[type].savedData.storage!.filename;
      }
      showPdfInNewTab(base64Data, fileName);
    } else {
      const link =
        documentHandlerMap.getter[type].linkUrl ||
        documentHandlerMap.getter[type].savedData.link;
      if (link) {
        window.open(link, '_blank');
      }
    }
  };

  if (isLoading) {
    return <MSkeleton height="51" />;
  }

  return (
    <MStack w="full" data-testid="default-msa">
      <MAccordion
        multiple
        w="full"
        value={accordionValue}
        onValueChange={({ value }) => handleAccordionChange(value)}
      >
        <MAccordionItem
          value="0"
          id={QUOTE_SETTING_KEYS.quoteDocuments}
          {...getHighlightStyles(isDocsHighlighted)}
          data-testid="default-msa-accordion"
          borderWidth="1px"
        >
          <DefaultQuoteDocumentsBody
            enableTenantQuoteDocumentManagement={
              enableTenantQuoteDocumentManagement
            }
            enableQuoteDocumentManagement={enableQuoteDocumentManagement}
            isUploadingDocument={isUploadingDocument}
            msaData={msaData}
            pdfCoverData={pdfCoverData}
            pdfFooterData={pdfFooterData}
            handleActiveItemChange={handleActiveItemChange}
            handleAddItem={handleAddItem}
            handleRemoveItem={handleRemoveItem}
            handleGoToItem={handleGoToItem}
          />
        </MAccordionItem>
      </MAccordion>
    </MStack>
  );
};

export default DefaultQuoteDocuments;
